import React from 'react';
import PropTypes from 'prop-types';
import {
  LuArrowRight,
  LuArrowUpRight,
  LuBookOpen,
  LuCalendarDays,
  LuFolderKanban,
  LuListChecks,
  LuMail,
  LuMegaphone,
  LuPlay,
  LuSparkles,
  LuTriangleAlert,
  LuX,
} from 'react-icons/lu';
import logo from '../../../../assets/logo.png';
import GlassIcon from '../../../../components/ui/GlassIcon';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { abs } from '../engine/stage';
import { POPULAR_TOPICS } from '../chapters';
import { DASH } from '../layout';
import AppFrame from './AppFrame';
import { Avatar, CARD, GLASS_CHIP } from './kit';
import { ProjectDetailDialog, ProjectsDialog, ReminderDialog, TasksDialog } from './DashboardDialogs';

const LINKS = [
  { key: 'calendar', icon: LuCalendarDays, gradient: 'tw-from-blue-500 tw-to-indigo-500 tw-shadow-blue-500/30' },
  { key: 'projects', icon: LuFolderKanban, gradient: 'tw-from-violet-500 tw-to-fuchsia-500 tw-shadow-violet-500/30' },
  { key: 'announcements', icon: LuMegaphone, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  { key: 'manual', icon: LuBookOpen, gradient: 'tw-from-emerald-500 tw-to-teal-500 tw-shadow-emerald-500/30' },
];


const Hero = () => {
  const { t, locale } = useTranslation();
  return (
    <div style={abs(DASH.hero)} className={`tw-absolute tw-overflow-hidden tw-rounded-[2rem] tw-px-9 tw-py-5 ${CARD}`}>
      <span className="tw-absolute -tw-left-24 -tw-top-28 tw-h-72 tw-w-72 tw-rounded-full tw-bg-teal-300/50 tw-blur-3xl dark:tw-bg-teal-500/20" />
      <span className="tw-absolute -tw-right-16 -tw-top-20 tw-h-64 tw-w-64 tw-rounded-full tw-bg-violet-300/40 tw-blur-3xl dark:tw-bg-violet-600/20" />
      <div className="tw-relative tw-flex tw-items-center tw-justify-between">
        <div>
          <span className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none`}>
            <LuSparkles aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          <p className="tw-mt-2 tw-text-[2rem] tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-slate-900 dark:tw-text-white">
            {t('common.greeting.afternoon')},{' '}
            <span className="tw-bg-gradient-to-r tw-from-teal-500 tw-via-cyan-500 tw-to-violet-500 tw-bg-clip-text tw-text-transparent">Ayşe Kaya</span>
          </p>
          <p className="tw-mt-1 tw-line-clamp-2 tw-max-w-[600px] tw-text-[13px] tw-leading-5 tw-text-slate-600 dark:tw-text-slate-400">{t('dashboard.subtitle')}</p>
        </div>
        <img src={logo} alt="" className="tw-h-14 tw-w-auto" />
      </div>
    </div>
  );
};

const TasksBanner = ({ unseen }) => {
  const { t } = useTranslation();
  return (
    <div style={abs(DASH.tasks)} className={`tw-absolute tw-flex tw-items-center tw-gap-4 tw-rounded-[1.75rem] tw-px-6 ${CARD} ${unseen ? 'tw-ring-2 tw-ring-rose-400/40' : ''}`}>
      <span className="tw-relative">
        <GlassIcon gradient={unseen ? 'tw-from-rose-400 tw-to-orange-500 tw-shadow-rose-500/30' : 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30'} size="md">
          <LuListChecks aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
        {unseen ? <span className="tw-absolute -tw-right-1 -tw-top-1 tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1 tw-text-[10px] tw-font-bold tw-text-white tw-ring-2 tw-ring-white">{unseen}</span> : null}
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-flex tw-items-center tw-gap-2 tw-text-[17px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">
          {t('dashboard.tasks.title')}
          {unseen ? <span className="tw-rounded-full tw-bg-rose-500 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-white">{t('dashboard.tasks.newBadge', { count: unseen })}</span> : null}
        </p>
        <p className="tw-mt-0.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{unseen ? t('dashboard.tasks.unseen', { count: unseen }) : t('dashboard.tasks.calm')}</p>
      </div>
      <span style={{ width: DASH.tasksButton.w, height: DASH.tasksButton.h }} className="tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
        {t('common.view')}
        <LuArrowRight aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
    </div>
  );
};

TasksBanner.propTypes = { unseen: PropTypes.number };

/** The new guide card (as on the real dashboard). */
const GuideCard = ({ highlightChip }) => {
  const { t } = useTranslation();
  return (
    <div style={abs(DASH.guide)} className="tw-absolute tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-teal-500/25 tw-bg-gradient-to-r tw-from-teal-500/[0.12] tw-via-cyan-500/[0.08] tw-to-indigo-500/[0.08] dark:tw-border-teal-400/20">
      <span style={{ left: DASH.guidePlay.x - DASH.guide.x, top: DASH.guidePlay.y - DASH.guide.y, width: DASH.guidePlay.w, height: DASH.guidePlay.h }} className="tw-absolute tw-grid tw-place-items-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-teal-500 tw-to-indigo-600 tw-shadow-lg tw-shadow-teal-500/30">
        <span className="tw-grid tw-h-11 tw-w-11 tw-place-items-center tw-rounded-full tw-bg-white tw-text-teal-600 tw-shadow-xl">
          <LuPlay aria-hidden="true" className="tw-ml-0.5 tw-h-5 tw-w-5" />
        </span>
      </span>
      <div style={{ left: 164, top: 14, right: 160 }} className="tw-absolute">
        <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-teal-700 dark:tw-text-teal-300">{t('userGuide.card.eyebrow', { minutes: 15 })}</p>
        <p className="tw-mt-0.5 tw-text-[17px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t('userGuide.card.title')}</p>
      </div>
      <div style={{ left: DASH.guideChips.x - DASH.guide.x, top: DASH.guideChips.y - DASH.guide.y }} className="tw-absolute tw-flex tw-gap-2">
        {POPULAR_TOPICS.map((chip) => (
          <span key={chip} className={`tw-inline-flex tw-h-7 tw-items-center tw-rounded-full tw-px-3 tw-text-xs tw-font-semibold tw-transition-colors tw-duration-300 ${chip === highlightChip ? 'tw-bg-teal-500 tw-text-white' : `${GLASS_CHIP} tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none dark:tw-text-slate-200`}`}>
            {t(`userGuide.chapters.${chip}`)}
          </span>
        ))}
      </div>
      <span style={{ right: 20, top: 36 }} className="tw-absolute tw-inline-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-px-5 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
        <LuPlay aria-hidden="true" className="tw-h-4 tw-w-4" />
        {t('userGuide.card.watch')}
      </span>
    </div>
  );
};

GuideCard.propTypes = { highlightChip: PropTypes.string };

const QuickLinks = ({ hover }) => {
  const { t } = useTranslation();
  return (
    <>
      <span style={abs(DASH.linksTitle)} className="tw-absolute tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-slate-500 dark:tw-text-slate-400">{t('dashboard.quickLinks')}</span>
      {LINKS.map((link, index) => {
        const Icon = link.icon;
        return (
          <div key={link.key} style={abs(DASH.link(index))} className={`tw-absolute tw-flex tw-flex-col tw-rounded-[1.75rem] tw-p-4 tw-transition-transform tw-duration-300 ${CARD} ${hover === link.key ? '-tw-translate-y-1 tw-ring-2 tw-ring-teal-400/50' : ''}`}>
            <GlassIcon gradient={link.gradient} size="xs">
              <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </GlassIcon>
            <p className="tw-mt-2 tw-line-clamp-2 tw-text-[15px] tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{t(`dashboard.links.${link.key}.title`)}</p>
            <p className="tw-mt-0.5 tw-truncate tw-text-[12px] tw-text-slate-500 dark:tw-text-slate-400">{t(`dashboard.links.${link.key}.description`)}</p>
            <span className={`tw-mt-auto tw-inline-flex tw-h-7 tw-w-fit tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-text-xs tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none`}>
              {t('common.view')}
              <LuArrowUpRight aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            </span>
          </div>
        );
      })}
    </>
  );
};

QuickLinks.propTypes = { hover: PropTypes.string };

export const DEADLINE = {
  banner: { x: 256, y: 72, w: 1000, h: 70 },
  complete: { x: 1082, y: 88, w: 116, h: 36 },
};

/** Red banner for SOPs / trainings about to expire (AssignmentAlerts). */
const DeadlineBanner = () => {
  const { t } = useTranslation();
  return (
    <div style={abs(DEADLINE.banner)} className="tw-absolute tw-z-[4] tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-rose-500/30 tw-bg-rose-50 tw-px-5 dark:tw-bg-rose-500/10">
      <LuTriangleAlert aria-hidden="true" className="tw-h-6 tw-w-6 tw-shrink-0 tw-text-rose-500" />
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-text-sm tw-font-semibold tw-text-rose-800 dark:tw-text-rose-200">{t('userGuide.stage.deadlineTitle')}</p>
        <p className="tw-truncate tw-text-xs tw-text-rose-700/80 dark:tw-text-rose-200/80">{t('userGuide.stage.deadlineItems')}</p>
      </div>
      <span style={{ width: DEADLINE.complete.w, height: DEADLINE.complete.h }} className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-rose-600 tw-text-sm tw-font-semibold tw-text-white">
        {t('userGuide.stage.complete')}
        <LuArrowRight aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      <LuX aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-rose-400" />
    </div>
  );
};

export const MESSAGE_TOAST = { x: 896, y: 590, w: 364, h: 104 };

/** Incoming-message card (bottom right). */
export const MessageToast = ({ show }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{ ...abs(MESSAGE_TOAST), opacity: show ? 1 : 0, transform: `translateY(${show ? 0 : 16}px)` }}
      className="tw-absolute tw-z-[8] tw-flex tw-gap-3 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-white/80 tw-bg-white tw-p-4 tw-shadow-2xl tw-transition-all tw-duration-500 dark:tw-border-white/10 dark:tw-bg-[#262a34]"
    >
      <span className="tw-relative tw-h-fit">
        <Avatar initials="MD" size={44} gradient="tw-from-violet-400 tw-to-indigo-600" />
        <span className="tw-absolute -tw-bottom-1 -tw-right-1 tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-teal-500 tw-text-white tw-ring-2 tw-ring-white">
          <LuMail aria-hidden="true" className="tw-h-3 tw-w-3" />
        </span>
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">Mehmet Demir</p>
        <p className="tw-text-[11px] tw-text-slate-400">mehmet.demir@habcarecro.com</p>
        <p className="tw-mt-1 tw-truncate tw-text-[13px] tw-text-slate-600 dark:tw-text-slate-300">{t('userGuide.stage.messagePreview')}</p>
      </div>
      <span className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-1 tw-bg-teal-500/70" />
    </div>
  );
};

MessageToast.propTypes = { show: PropTypes.bool };

/**
 * The dashboard of a regular employee, with its dialogs.
 * state: { unseen, hoverLink, highlightChip, deadline, message, frame,
 *          overlay: 'tasks'|'projects'|'projectDetail'|'reminder', appear, tasks, projects, reminder }
 */
const DashboardScene = ({ state }) => {
  const shift = state.deadline ? 82 : 0;
  return (
    <AppFrame frame={{ page: 'dashboard', unseenTasks: state.unseen || 0, ...state.frame }}>
      {state.deadline && <DeadlineBanner />}
      <div style={{ transform: `translateY(${shift}px)` }} className="tw-absolute tw-inset-0">
        <Hero />
        <TasksBanner unseen={state.unseen} />
        <GuideCard highlightChip={state.highlightChip} />
        <QuickLinks hover={state.hoverLink} />
      </div>
      <MessageToast show={Boolean(state.message)} />
      {(state.overlay === 'tasks') && <TasksDialog state={state.tasks || {}} appear={state.appear ?? 1} />}
      {(state.overlay === 'projects' || state.overlay === 'projectDetail') && <ProjectsDialog state={state.projects || {}} appear={state.appear ?? 1} />}
      {state.overlay === 'projectDetail' && <ProjectDetailDialog appear={state.detailAppear ?? 1} />}
      {state.overlay === 'reminder' && <ReminderDialog state={state.reminder || {}} appear={state.appear ?? 1} />}
    </AppFrame>
  );
};

DashboardScene.propTypes = { state: PropTypes.object.isRequired };

export default DashboardScene;
